import moment from 'moment';
import { CremationStatus } from '../types';
import { formatPhone, isNanpNumber } from '../lib/phone';
import { CallContext, asCallContext, callContextLine } from './callContext';

export type InboxChannel = 'sms' | 'call' | 'voicemail' | 'email';
export type CallStatus = 'answered' | 'missed' | 'voicemail';

/** One row of the `inbox_items` view — a conversation, latest activity first. */
export interface InboxItem {
  id: string;
  conversation_key: string;
  last_message_id: string;
  lead_id: string | null;
  funeral_id: number | null;
  last_message_at: string;
  last_message_direction: 'inbound' | 'outbound';
  last_message_body: string | null;
  last_message_channel: InboxChannel;
  from_identifier: string | null;
  to_identifier: string | null;
  prefer_funeral: boolean;
  region: 'quebec' | 'ontario' | null;
  language: 'en' | 'fr' | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  is_unreplied: boolean;
  client_id: number | null;
  owner_id: string | null;
  owner_name: string | null;
  deceased_name: string | null;
  cremation_status: string | null;
  last_call_status: CallStatus | null;
  last_call_duration_seconds: number | null;
  snoozed_until: string | null;
  /**
   * Who the conversation belongs to: the case owner, else the lead owner —
   * only while that person is active. Null = unclaimed.
   */
  assignee_id: string | null;
  assignee_name: string | null;
  /**
   * ON | QC_EN | QC_FR: the case's region + language, else the lead's stored
   * market (leads.market). Null = unknown, which every market sees.
   */
  market: string | null;
  /**
   * What we knew about the latest call (communications.metadata.call_context,
   * T-058): menu hang-up kind, line, seconds on the line, after hours, ad
   * session. Null for SMS, older calls, or before 20260926010000 has run.
   */
  last_call_context?: CallContext | null;
  /**
   * The latest message is a quiet IVR menu hang-up (an unknown, non-ad number
   * that hung up in the menu once): not needs-reply. Absent before
   * 20260926010000 has run.
   */
  is_menu_hangup?: boolean;
  /** The heading email's subject. Absent before 20261103200000 has run. */
  last_message_subject?: string | null;
  /** An email from someone nobody has placed; keyed `email:<address>` (T-236). */
  is_unmatched?: boolean;
  /** family | partner | unmatched | reply; null unless the heading is an email. */
  last_email_kind?: string | null;
}


/** Who the conversation belongs to: a case, else a lead, else a bare client. */
export type ConversationScope = 'funeral' | 'lead' | 'client';

export function conversationScope(item: InboxItem): ConversationScope {
  if (item.funeral_id != null) return 'funeral';
  if (item.lead_id) return 'lead';
  return 'client';
}

/**
 * The record a conversation opens in a drawer: its case, else its lead. A bare
 * client has neither, so nothing to open.
 */
export type RecordTarget = { kind: 'funeral'; id: number } | { kind: 'lead'; id: string };

export function recordTarget(item: InboxItem): RecordTarget | null {
  if (item.funeral_id != null) return { kind: 'funeral', id: item.funeral_id };
  if (item.lead_id) return { kind: 'lead', id: item.lead_id };
  return null;
}

export function sameRecord(a: RecordTarget | null, b: RecordTarget | null): boolean {
  return !!a && !!b && a.kind === b.kind && String(a.id) === String(b.id);
}

export function formatDuration(seconds: number | null): string {
  if (!seconds || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * The preview for a conversation row, in parts so the row can style them:
 * `prefix` is quiet ("You:"), `emphasis` is what happened when it isn't a
 * message ("Missed call", "Voicemail"), `text` is the rest.
 *
 * Calls carry no body, so the view leaves `last_message_body` null for them and
 * the preview is composed here rather than synthesised into SQL — the view stays
 * a description of what happened, not of how we phrase it.
 */
export interface PreviewParts {
  prefix: string | null;
  emphasis: string | null;
  text: string;
}

export function conversationPreview(
  item: InboxItem,
  { full = false }: { full?: boolean } = {},
): PreviewParts {
  const inbound = item.last_message_direction === 'inbound';
  const body = item.last_message_body?.replace(/\s+/g, ' ').trim();

  // An email reads by its subject: the body is mostly signature and quoted mail.
  if (item.last_message_channel === 'email') {
    const subject = item.last_message_subject?.replace(/\s+/g, ' ').trim();
    return { prefix: inbound ? null : 'You:', emphasis: null, text: subject || body || '(no subject)' };
  }

  // What the webhook recorded about the call (T-058): "Hung up in menu · 12 s
  // · Ad call: cremation montreal". `full` adds the ad's landing page and
  // campaign, for the toast. Rows without it read as before.
  const context =
    item.last_message_channel === 'sms'
      ? null
      : callContextLine(
          asCallContext(item.last_call_context),
          {
            direction: item.last_message_direction,
            status: item.last_message_channel === 'voicemail' ? 'voicemail' : item.last_call_status,
            durationSeconds: item.last_call_duration_seconds,
          },
          { full },
        );
  const contextTail = context?.rest.length ? ` · ${context.rest.join(' · ')}` : '';

  if (item.last_message_channel === 'voicemail') {
    return {
      prefix: null,
      emphasis: 'Voicemail',
      text: `${contextTail}${body ? ` · “${body}”` : ''}`,
    };
  }

  if (item.last_message_channel === 'call') {
    if (context && inbound) {
      if (item.last_call_status === 'answered') {
        const dur = formatDuration(item.last_call_duration_seconds);
        return { prefix: null, emphasis: null, text: `Call · ${dur} · ${context.outcome}` };
      }
      return { prefix: null, emphasis: context.outcome, text: contextTail };
    }
    if (item.last_call_status !== 'answered') {
      return inbound
        ? { prefix: null, emphasis: 'Missed call', text: '' }
        : { prefix: null, emphasis: null, text: 'Called, no answer' };
    }
    const dur = formatDuration(item.last_call_duration_seconds);
    return { prefix: null, emphasis: null, text: `${inbound ? 'Call' : 'You called'} · ${dur}` };
  }

  return { prefix: inbound ? null : 'You:', emphasis: null, text: body || '(no content)' };
}

/**
 * The case a conversation sits on: "Jean Tremblay · #847". Leads get nothing —
 * the row's Lead label already says there's no case, so saying it twice is
 * noise.
 */
export function conversationContext(item: InboxItem): string | null {
  if (item.funeral_id == null) return null;
  return item.deceased_name
    ? `${item.deceased_name} · #${item.funeral_id}`
    : `Case #${item.funeral_id}`;
}

/**
 * Short pill labels for a case's stage. The case tracker spells these out
 * ("Cremation Scheduled"); the inbox row only has room for the gist.
 */
const STAGE_LABEL: Record<CremationStatus, string> = {
  [CremationStatus.ARRANGEMENT]: 'Arrangement',
  [CremationStatus.TRANSPORT_DISPATCHED]: 'Transport',
  [CremationStatus.CREMATION_SCHEDULED]: 'Scheduled',
  [CremationStatus.READY_FOR_DELIVERY]: 'Ready',
  [CremationStatus.DELIVERY_SCHEDULED]: 'Delivery',
  [CremationStatus.DELIVERED]: 'Delivered',
};

/**
 * Case stages share the customer green, except Arrangement: those cases carry
 * the most live conversation and the most time-sensitive, so they stand out.
 * Delivered goes quiet — the work is done.
 */
export function stageTone(status: string | null): string {
  if (!status || status === CremationStatus.ARRANGEMENT) return 'bg-sky-100 text-sky-800';
  if (status === CremationStatus.DELIVERED) return 'bg-muted text-muted-foreground';
  return 'bg-primary/10 text-primary';
}

/** A case with no status yet is in arrangement, as the case tracker treats it. */
export function stageLabel(status: string | null): string {
  if (!status) return STAGE_LABEL[CremationStatus.ARRANGEMENT];
  return STAGE_LABEL[status as CremationStatus] ?? status.replace(/_/g, ' ');
}

/** Names that are really a phone number, as unnamed conversations carry. */
const PHONE_LIKE = /^\+?[\d\s().-]{7,}$/;

/**
 * Who the row is with: their name, else their number formatted for reading.
 * `named` is false when there's only a number, so the avatar can skip initials.
 */
export function contactLabel(item: InboxItem): { label: string; named: boolean } {
  const name = item.contact_name?.trim();
  if (name && !PHONE_LIKE.test(name)) return { label: name, named: true };
  const raw = name || conversationPhone(item);
  if (raw) return { label: formatPhone(raw), named: false };
  return { label: item.contact_email?.trim() || 'Unknown', named: false };
}

/** "2:14 pm" today, then "Yesterday", a weekday for this week, then a date. */
export function rowTime(iso: string, now: moment.Moment = moment()): string {
  const t = moment(iso);
  if (t.isSame(now, 'day')) return t.format('h:mm a');
  if (t.isSame(now.clone().subtract(1, 'day'), 'day')) return 'Yesterday';
  if (now.diff(t, 'days') < 7) return t.format('ddd');
  if (t.isSame(now, 'year')) return t.format('MMM D');
  return t.format('MMM D, YYYY');
}

/**
 * The number to call this conversation back on, in E.164.
 *
 * Prefers the client record's phone, falling back to whichever identifier on
 * the last message was theirs — a conversation can exist before anyone has
 * tidied the client record, and a missed call from an unknown number is
 * exactly the case worth being able to return.
 */
export function conversationPhone(item: InboxItem): string | null {
  // An email's identifiers are addresses, and the digits in one are no number.
  if (item.last_message_channel === 'email' && !item.contact_phone) return null;
  const raw =
    item.contact_phone ||
    (item.last_message_direction === 'inbound' ? item.from_identifier : item.to_identifier);
  if (!raw) return null;

  const digits = raw.replace(/\D/g, '');
  if (raw.trim().startsWith('+')) return `+${digits}`;
  if (digits.length === 10) return isNanpNumber(digits) ? `+1${digits}` : raw.trim();
  if (digits.length === 11 && digits.startsWith('1')) return isNanpNumber(digits.slice(1)) ? `+${digits}` : raw.trim();
  // A leading 0 is a local number whose country we can't tell: dial it as typed.
  if (digits.startsWith('0')) return raw.trim();
  return digits ? `+${digits}` : null;
}

/**
 * A conversation's alert, in plain text — for the toast and the desktop
 * notification, built from the same helpers as the dock row so all three read
 * the same. `context` is the row's second line: the case, else "Lead", else
 * the no-case note.
 */
export function alertCopy(item: InboxItem): {
  title: string;
  context: string | null;
  preview: string;
} {
  const scope = conversationScope(item);
  const context =
    scope === 'funeral'
      ? conversationContext(item)
      : scope === 'lead'
        ? 'Lead'
        : 'Not linked to a case';
  const p = conversationPreview(item, { full: true });
  const preview = [p.prefix, `${p.emphasis ?? ''}${p.text}`].filter(Boolean).join(' ');
  return { title: contactLabel(item).label, context, preview };
}

/** The fields of a `communications` row the alert rule reads. */
export interface CommunicationInsert {
  id: string;
  channel: string | null;
  direction: string | null;
  call_status: string | null;
  created_at: string | null;
  metadata?: { call_context?: Partial<CallContext> | null } | null;
}

/** Older than this and an insert is a backfill or a replay, not news. */
export const ALERT_MAX_AGE_MS = 10 * 60 * 1000;

/**
 * Whether a new `communications` row is the kind that alerts (plan §1): the
 * family reaching out by SMS, or a call we didn't answer. The same rule
 * `is_unreplied` uses, so an alert never announces something the badge would
 * not count. Whose it is gets decided later, from the view.
 */
export function isAlertableInsert(c: CommunicationInsert, now = Date.now()): boolean {
  if (c.direction !== 'inbound') return false;
  const channelOk =
    c.channel === 'sms' ||
    ((c.channel === 'call' || c.channel === 'voicemail') && c.call_status !== 'answered');
  if (!channelOk) return false;
  // The webhook decided this call interrupts nobody (T-058): a quiet IVR menu
  // hang-up, or an ad-number hang-up after hours (still in needs-reply).
  if (c.metadata?.call_context?.notify === false) return false;
  const at = c.created_at ? Date.parse(c.created_at) : NaN;
  return Number.isFinite(at) && now - at < ALERT_MAX_AGE_MS;
}
